import { InterviewStageEnum } from 'consts/company-alias.consts'
import React, { ReactNode, useEffect, useMemo } from 'react'
import { Welcome } from './pages/welcome'
import { VariablesType } from 'constants/vacancy'
import { getVariables } from './pages/variables.utils'
import { LoadingPage } from './pages/loading'
import { InterviewPermissionsAndCall } from './interview-permissions-and-call.ui'
import { Results } from './pages/results'
import {
  InterviewRouterProps,
  InterviewRouterRichProps,
  InterviewStateFallbackProps
} from './interview-ui.types'
import { interviewFixturesService } from './interview-fixtures.service'
import { useInterview } from 'hooks/use-interview'
import { UnknownError } from './pages/unknown-error'
import { useUnit } from 'effector-react'
import { useInterviewState } from 'hooks/use-interview-status'
import { Route, QueryParams } from 'routes/$companyAlias/$positionAlias'
import { callWsStateService } from './pages/call/call-ws-state.service'
import { callWsService } from './pages/call/call-ws.service'
import { OutOfCredits } from './pages/out-of-credits'
import { InterviewStatus } from 'api/endpoints/openapi-generated.schemas'
import { callServiceErrorService } from './pages/call/call-service-error.service'
import { ServiceErrorOverlay } from './pages/call/components/service-error-overlay.ui'

export function InterviewRouter(props: InterviewRouterProps): ReactNode {
  const { interviewId, isSearchParamsWelcome } = props
  const { data, error, isLoading, refetch } = useInterview(interviewId)
  const serviceError = useUnit(callServiceErrorService.$serviceError)
  const debugForceOverlay = useUnit(callServiceErrorService.$debugForceOverlay)

  // the persisted interview status is the only source of truth for a fatal
  // service error - the WS service_error_message can arrive for a transient,
  // recoverable hiccup that the dialog server keeps going after (interview
  // stays in_progress), so it must never render the fatal overlay on its
  // own. It only forces an immediate re-check of the persisted status so a
  // genuine fatal error surfaces within seconds instead of waiting on the
  // regular poll interval or a manual refresh.
  const isPersistedServiceError =
    data?.data.status === InterviewStatus.service_error

  // the dialog server can reject a call for an already-terminated interview
  // (e.g. service_error) before it ever gets to send a service_error_message
  // over the socket - the client would otherwise just reconnect forever
  // without learning why. Re-checking the persisted status on every socket
  // drop catches that case within seconds instead of waiting on the regular
  // poll interval or a manual refresh.
  useEffect(() => {
    const subscription = callWsService.wsClosed.watch(() => {
      void refetch()
    })
    return () => subscription.unsubscribe()
  }, [refetch])

  useEffect(() => {
    if (!serviceError) return
    void refetch()
  }, [serviceError, refetch])

  if (isPersistedServiceError || debugForceOverlay) {
    return <ServiceErrorOverlay />
  }

  const isRenderWelcomeStep =
    !interviewId ||
    (isSearchParamsWelcome &&
      !!data &&
      (!data.data.status || data.data.status === 'not_started'))

  if (isRenderWelcomeStep) {
    return <Welcome {...props} />
  }

  if (isLoading || (!data && !error)) {
    console.log('interview get loading')
    return <LoadingPage />
  }

  if (error) {
    const isForbiddenError = error.status === 403

    // TODO тут кейс результатов а не out of credints
    if (isForbiddenError) {
      return <OutOfCredits />
    }

    return <UnknownError />
  }

  return (
    <InterviewStateFallback {...props} interview={data.data}>
      {(props: InterviewRouterRichProps) => (
        <InterviewPermissionsAndLaterRouter {...props} />
      )}
    </InterviewStateFallback>
  )
}

function InterviewStateFallback(props: InterviewStateFallbackProps): ReactNode {
  const { vacancy, interview, user } = props

  const initialMeetingMode = useUnit(
    interviewFixturesService.$initialMeetingMode
  )
  const initialMessages = useUnit(interviewFixturesService.$initialMessages)

  const forceResults = useUnit(callWsStateService.$isForceResults)
  const callEndReceived = useUnit(callWsStateService.$callEndReceived)

  const { data, error, isLoading } = useInterviewState(vacancy, interview)
  const { stage: routeStage } = Route.useSearch() as QueryParams

  const stage = useMemo(() => {
    const isInterviewStatusFinished =
      interview.status === InterviewStatus.completed ||
      interview.status === InterviewStatus.cancelled ||
      interview.status === InterviewStatus.incomplete ||
      interview.status === InterviewStatus.failed

    const isInterviewEnded =
      forceResults ||
      (!callEndReceived && isInterviewStatusFinished) ||
      error?.status === 404

    const getStage = (): InterviewStageEnum => {
      if (isInterviewEnded) {
        return InterviewStageEnum.Results
      }

      if (callEndReceived) {
        return InterviewStageEnum.Call
      }

      if (
        interview.status === 'not_started' ||
        interview.status === 'permissions_requested'
      ) {
        // нажали кнопку начать интервью
        if (routeStage === 'call') {
          return InterviewStageEnum.Call
        }
        return InterviewStageEnum.Permissions
      } else if (interview.status === 'in_progress') {
        // нажали завершить
        if (routeStage === 'results') {
          return InterviewStageEnum.Results
        }

        return InterviewStageEnum.Call
      } else {
        return InterviewStageEnum.Welcome
      }
    }

    const stage = getStage()

    if (window.isDevMode) {
      console.log(
        `(render) calculating InterviewStateFallback stage memo:,
          forceResults ${forceResults},
          callEndReceived ${callEndReceived},
          interview.status ${interview.status},
          routeStage ${routeStage},
          error.status ${error?.status},
          =>
          calculated stage ${stage}`
      )
    }

    return stage
  }, [
    callEndReceived,
    error?.status,
    forceResults,
    interview.status,
    routeStage
  ])

  const variables = useMemo((): VariablesType => {
    return getVariables(vacancy, interview)
  }, [vacancy, interview])

  if (isLoading || (!data && !error) || !initialMeetingMode) {
    return <LoadingPage />
  }

  if (error || !initialMessages) {
    if (stage !== InterviewStageEnum.Results) {
      return <UnknownError />
    }
  }

  return (
    <>
      {props.children({
        user,
        vacancy,
        interview,
        stage,
        variables,
        initialMessages: initialMessages || [],
        initialMeetingMode: initialMeetingMode
      })}
    </>
  )
}

function InterviewPermissionsAndLaterRouter(
  props: InterviewRouterRichProps
): ReactNode {
  const { vacancy, stage, variables, interview } = props

  return (
    <div>
      {(stage === InterviewStageEnum.Call ||
        stage === InterviewStageEnum.Permissions) && (
        <InterviewPermissionsAndCall {...props} />
      )}

      {stage === InterviewStageEnum.Results && (
        <Results
          vacancy={vacancy}
          interviewId={interview.id}
          variables={variables}
        />
      )}
    </div>
  )
}
